import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const Finances = ({ user }) => {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: 'expense',
    amount: 0,
    concept: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, transactionsRes] = await Promise.all([
        api.getBalance(),
        api.getTransactions(),
      ]);
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createTransaction(formData);
      toast.success('Transacción registrada');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al registrar transacción');
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'expense',
      amount: 0,
      concept: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-[#C9A961] text-xl font-cormorant">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="finances-page">
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-[#2C2C2C]">Finanzas</h1>
            <p className="text-[#6B6B6B] mt-1">Administra ingresos y gastos</p>
          </div>

          <Button
            onClick={() => setShowModal(true)}
            data-testid="create-transaction-button"
            className="bg-[#C9A961] hover:bg-[#B89551]"
          >
            <Plus className="mr-2" size={20} />
            Nueva Transacción
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-6 border-[#C9A961]/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#6B6B6B]">Ingresos Totales</span>
            <TrendingUp className="text-[#6B7C59]" size={20} />
          </div>
          <p className="text-3xl font-cormorant font-bold text-[#6B7C59]">
            $ {balance?.total_income?.toFixed(2) || 0}
          </p>
        </Card>

        <Card className="p-6 border-[#C9A961]/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#6B6B6B]">Gastos Totales</span>
            <TrendingDown className="text-red-500" size={20} />
          </div>
          <p className="text-3xl font-cormorant font-bold text-red-500">
            $ {balance?.total_expenses?.toFixed(2) || 0}
          </p>
        </Card>

        <Card className="p-6 border-[#C9A961]/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#6B6B6B]">Comisiones Pendientes</span>
            <DollarSign className="text-[#C9A961]" size={20} />
          </div>
          <p className="text-3xl font-cormorant font-bold text-[#C9A961]">
            $ {balance?.total_pending_commissions?.toFixed(2) || 0}
          </p>
        </Card>

        <Card className="p-6 border-[#C9A961]/20 bg-[#C9A961]/5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#6B6B6B]">Balance Neto</span>
            <DollarSign className="text-[#2C2C2C]" size={20} />
          </div>
          <p className="text-3xl font-cormorant font-bold text-[#2C2C2C]">
            $ {balance?.balance?.toFixed(2) || 0}
          </p>
        </Card>
      </div>

      {/* Transactions List */}
      <Card className="border-[#C9A961]/20">
        <div className="p-6 border-b border-[#C9A961]/20">
          <h2 className="text-xl font-cormorant font-semibold text-[#2C2C2C]">Transacciones Recientes</h2>
        </div>
        <div className="divide-y divide-[#C9A961]/10">
          {transactions.length === 0 ? (
            <div className="p-8 text-center text-[#6B6B6B]">
              No hay transacciones registradas
            </div>
          ) : (
            transactions.map((transaction) => (
              <div
                key={transaction.id}
                className="p-4 hover:bg-[#F5F2ED] transition-colors"
                data-testid={`transaction-${transaction.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        transaction.type === 'income'
                          ? 'bg-[#6B7C59]/20 text-[#6B7C59]'
                          : 'bg-red-100 text-red-500'
                      }`}
                    >
                      {transaction.type === 'income' ? (
                        <TrendingUp size={20} />
                      ) : (
                        <TrendingDown size={20} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-[#2C2C2C]">{transaction.concept}</p>
                      <p className="text-sm text-[#6B6B6B]">
                        {format(parseISO(transaction.date), 'dd MMM yyyy', { locale: es })}
                        {transaction.category && ` • ${transaction.category}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-lg font-semibold ${
                        transaction.type === 'income' ? 'text-[#6B7C59]' : 'text-red-500'
                      }`}
                    >
                      {transaction.type === 'income' ? '+' : '-'} $ {transaction.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-[#6B6B6B]">{transaction.currency}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Transaction Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="transaction-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-cormorant font-bold text-[#C9A961]">
              Nueva Transacción
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-[#2C2C2C] font-medium">Tipo *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger className="mt-1.5" data-testid="transaction-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Ingreso</SelectItem>
                  <SelectItem value="expense">Gasto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#2C2C2C] font-medium">Monto ($) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                data-testid="transaction-amount-input"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label className="text-[#2C2C2C] font-medium">Concepto *</Label>
              <Input
                value={formData.concept}
                onChange={(e) => setFormData({ ...formData, concept: e.target.value })}
                data-testid="transaction-concept-input"
                className="mt-1.5"
                required
              />
            </div>

            <div>
              <Label className="text-[#2C2C2C] font-medium">Categoría</Label>
              <Input
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="mt-1.5"
                placeholder="Ej: servicios, productos, varios"
              />
            </div>

            <div>
              <Label className="text-[#2C2C2C] font-medium">Fecha *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1.5"
                required
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#C9A961] hover:bg-[#B89551]">
                Guardar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Finances;